import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import axios from 'utils/axios';
import { format } from 'date-fns';

// MUI imports
import { Box, Button, CircularProgress, InputAdornment, LinearProgress, MenuItem, TextField, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { grey } from '@mui/material/colors';
import useMediaQuery from '@mui/material/useMediaQuery';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import CheckIcon from '@mui/icons-material/Check';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

// Project imports
import { useDispatch } from 'store';
import { showErrorAlert } from 'utils/global-functions';
import {
    Subject,
    AssessmentTypeLabel,
    assessmentTypesForSubject,
    PHONE_TAP_TARGET,
    READING_SCORES,
    ROW_GRID,
    termNotStarted
} from './constants';

export default function StudentAssessmentRow({ row, term, onSaved, onSavingChange }) {
    const theme = useTheme();
    // Before the term's first day the fields are shown but can't be changed, as the server would refuse the save
    const locked = termNotStarted(term);
    const navigate = useNavigate();
    // Below desktop there is no column header, so the fields carry their own labels
    const compact = useMediaQuery(theme.breakpoints.down('lg'));
    const dispatch = useDispatch();

    const [type, setType] = useState(row.assessmentType ?? '');
    const [score, setScore] = useState(row.score ?? '');
    const [saving, setSaving] = useState(false);
    const [justSaved, setJustSaved] = useState(false);

    // One save at a time per row, so the server sees writes in the order they were made and never creates the term's
    // assessment twice. Changes made during a save wait in `queued`; only the newest is sent next.
    const inFlight = useRef(false);
    const queued = useRef(null);
    // What the server last confirmed, and what this row last sent
    const confirmed = useRef({ type: row.assessmentType ?? '', score: row.score ?? '' });
    const lastSent = useRef(confirmed.current);
    const mounted = useRef(true);
    const savedTimer = useRef();

    useEffect(
        () => () => {
            mounted.current = false;
            clearTimeout(savedTimer.current);
        },
        []
    );

    // Values from outside this row (another row for the same student, or a reload) replace what's shown, but this
    // row's own echoed answers don't, so typing isn't reset while a save comes back
    useEffect(() => {
        const incoming = { type: row.assessmentType ?? '', score: row.score ?? '' };
        confirmed.current = incoming;
        if (
            !inFlight.current &&
            !queued.current &&
            (incoming.type !== lastSent.current.type || incoming.score !== lastSent.current.score)
        ) {
            lastSent.current = incoming;
            setType(incoming.type);
            setScore(incoming.score);
        }
    }, [row.assessmentType, row.score]);

    const send = async (values) => {
        inFlight.current = true;
        lastSent.current = values;
        try {
            const { data } = await axios.post('/assessments/save', {
                studentId: row.studentId,
                subject: row.subject,
                termId: term.id,
                assessmentType: values.type === '' ? null : values.type,
                score: values.score === '' ? null : values.score
            });
            confirmed.current = { type: data.assessmentType ?? '', score: data.score ?? '' };
            onSaved(row, data);
            if (!queued.current && mounted.current) {
                setJustSaved(true);
                clearTimeout(savedTimer.current);
                savedTimer.current = setTimeout(() => mounted.current && setJustSaved(false), 2500);
            }
        } catch (ex) {
            showErrorAlert(dispatch, ex);
            if (!queued.current && mounted.current) {
                // Show what the server actually has
                lastSent.current = confirmed.current;
                setType(confirmed.current.type);
                setScore(confirmed.current.score);
            }
        } finally {
            inFlight.current = false;
            if (queued.current) {
                const next = queued.current;
                queued.current = null;
                send(next);
            } else {
                if (mounted.current) setSaving(false);
                onSavingChange(false);
            }
        }
    };

    const save = (nextType, nextScore) => {
        const values = { type: nextType, score: nextScore };
        if (inFlight.current) {
            queued.current = values;
            return;
        }
        setSaving(true);
        setJustSaved(false);
        onSavingChange(true);
        send(values);
    };

    const isValidMathScore = (value) => value === '' || /^(100|[1-9]?\d)$/.test(value);

    const onTypeChange = (e) => {
        setType(e.target.value);
        // A half-typed Math score isn't sent with a type change; the last sent score is
        const draft = `${score}`.trim();
        const scoreToSend = row.subject === Subject.Math && !isValidMathScore(draft) ? lastSent.current.score : draft;
        save(e.target.value, scoreToSend);
    };

    const onScoreBlur = () => {
        const trimmed = `${score}`.trim();
        const pending = queued.current ?? lastSent.current;
        if (trimmed === pending.score) return;
        if (row.subject === Subject.Math && !isValidMathScore(trimmed)) {
            showErrorAlert(dispatch, 'Math score must be a whole number from 0 to 100');
            setScore(pending.score);
            return;
        }
        save(type, trimmed);
    };

    const onReadingScoreChange = (e) => {
        setScore(e.target.value);
        save(type, e.target.value);
    };

    const openGoals = () =>
        navigate('/assessment', {
            state: { studentId: row.studentId, subject: row.subject, termId: term.id, term: term.term, termStartDate: term.startDate }
        });

    const hasChecklist = Boolean(row.grade) && row.standardCount > 0;
    // Only this term's saved goals are counted, so there's no count until they're saved
    const goalsSaved = row.ratedCount !== null && row.ratedCount !== undefined;
    let goalsText = '';
    if (!row.grade) {
        goalsText = 'Needs a grade';
    } else if (row.standardCount === 0) {
        goalsText = 'No checklist for this grade yet';
    } else if (goalsSaved) {
        goalsText = `${row.ratedCount} of ${row.standardCount} marked`;
    }

    // Faint outline until hovered or focused, so the rows read as text rather than a grid of boxes
    const quietInput = {
        '& .MuiOutlinedInput-root': {
            '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.grey[200] },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.grey[400] },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main }
        },
        '& .MuiInputBase-input': { fontWeight: 400 }
    };

    // The block needs a type as well as a score, so a saved score with no type marks the type as the missing piece
    const typeMissing = type === '' && Boolean(row.typeRequired);

    const placeholder = (text) => (
        <Box component="span" sx={{ color: theme.palette.grey[400] }}>
            {text}
        </Box>
    );

    // The most recent earlier term's score is a reminder under the empty field. It never counts as this term's.
    // It's the field's helper text, so screen readers read it with the field.
    const previousHint = row.previousScore && row.previousTerm && score === '' ? `Term ${row.previousTerm}: ${row.previousScore}` : null;
    // Ids for the helper texts; the group is included because a student can be in two groups for a subject
    const fieldId = `${row.groupId}-${row.studentId}-${row.subject}`;
    // Mid grey (#757575, about 4.6:1 on white). The theme's grey[600] is near-black. Set for the disabled state too
    // (a locked future term), or MUI's faint disabled colour wins.
    const hintProps = { sx: { mx: 0, mt: 0.25, fontSize: 11, color: grey[600], '&.Mui-disabled': { color: grey[600] } } };

    return (
        <Box
            sx={{
                ...ROW_GRID,
                // Top-aligned, so the fields stay in line when one of them has a hint or "Type is required" under it
                alignItems: 'start',
                px: { xs: 2, sm: 3 },
                py: { xs: 2, lg: 1.25 },
                '& + &': { borderTop: { xs: `1px solid ${theme.palette.grey[100]}`, lg: 0 } }
            }}
        >
            <Box sx={{ minWidth: 0, gridArea: 'student' }}>
                <Typography fontSize={14} sx={{ color: theme.palette.grey[900] }} noWrap>
                    {row.studentName}
                </Typography>
                <Typography fontSize={12} sx={{ color: theme.palette.grey[500] }}>
                    Joined {row.joinedOn ? format(new Date(row.joinedOn), 'MMM d, yyyy') : '—'}
                </Typography>
            </Box>

            <TextField
                select
                fullWidth
                size="small"
                id={`type-${fieldId}`}
                value={type}
                onChange={onTypeChange}
                disabled={locked}
                required={typeMissing}
                error={typeMissing}
                helperText={typeMissing ? 'Type is required' : undefined}
                label={compact ? 'Assessment type' : undefined}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'aria-label': 'Assessment type' }}
                sx={{ ...quietInput, gridArea: 'type' }}
                SelectProps={{
                    displayEmpty: true,
                    renderValue: (v) => (v === '' ? placeholder('Select type') : AssessmentTypeLabel[v])
                }}
            >
                <MenuItem value="">
                    <em>None</em>
                </MenuItem>
                {assessmentTypesForSubject(row.subject).map((t) => (
                    <MenuItem key={t} value={t}>
                        {AssessmentTypeLabel[t]}
                    </MenuItem>
                ))}
            </TextField>

            <Box sx={{ gridArea: 'score', minWidth: 0 }}>
                {row.subject === Subject.Math ? (
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="0–100"
                        id={`score-${fieldId}`}
                        helperText={previousHint}
                        FormHelperTextProps={hintProps}
                        value={score}
                        disabled={locked}
                        onChange={(e) => setScore(e.target.value.replace(/[^0-9]/g, ''))}
                        onBlur={onScoreBlur}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') e.target.blur();
                        }}
                        sx={quietInput}
                        label={compact ? 'Score' : undefined}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                            // Text, not type=number: the mouse wheel can't change it, and phones still show a number pad
                            inputProps: { inputMode: 'numeric', maxLength: 3, 'aria-label': 'Score' },
                            endAdornment: <InputAdornment position="end">%</InputAdornment>
                        }}
                    />
                ) : (
                    <TextField
                        select
                        fullWidth
                        size="small"
                        id={`score-${fieldId}`}
                        helperText={previousHint}
                        FormHelperTextProps={hintProps}
                        value={score}
                        disabled={locked}
                        onChange={onReadingScoreChange}
                        sx={quietInput}
                        label={compact ? 'Score' : undefined}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ 'aria-label': 'Score' }}
                        SelectProps={{ displayEmpty: true, renderValue: (v) => (v === '' ? placeholder('Score') : v) }}
                    >
                        <MenuItem value="">
                            <em>None</em>
                        </MenuItem>
                        {READING_SCORES.map((s) => (
                            <MenuItem key={s} value={s}>
                                {s}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
            </Box>

            <Box
                sx={{
                    minWidth: 0,
                    gridArea: 'goals',
                    // As tall as a field, so the goals line up with the fields' text
                    minHeight: 40,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                }}
            >
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1, rowGap: 0.5 }}>
                        {row.blocked && (
                            <Box
                                component="span"
                                role="status"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 999,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    lineHeight: 1.4,
                                    whiteSpace: 'nowrap',
                                    // Dark red text (about 5:1 on the pale fill); the fill and border keep the prototype look
                                    color: theme.palette.error.dark,
                                    backgroundColor: alpha(theme.palette.error.main, 0.08),
                                    border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`
                                }}
                            >
                                <WarningAmberRoundedIcon sx={{ fontSize: 13 }} aria-hidden /> Billing Blocked
                            </Box>
                        )}
                        <Typography
                            fontSize={13}
                            sx={{ minWidth: 0, color: row.ratedCount ? theme.palette.grey[800] : theme.palette.grey[500] }}
                        >
                            {goalsText}
                        </Typography>
                        {saving && <CircularProgress size={14} aria-label="Saving" />}
                        {!saving && justSaved && (
                            <Box
                                component="span"
                                role="status"
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.25, fontSize: 12, color: theme.palette.success.dark }}
                            >
                                <CheckIcon sx={{ fontSize: 16 }} /> Saved
                            </Box>
                        )}
                    </Box>
                    {hasChecklist && goalsSaved && (
                        <LinearProgress
                            variant="determinate"
                            value={(100 * (row.ratedCount || 0)) / row.standardCount}
                            aria-label="Goals marked"
                            sx={{ mt: 0.75, height: 4, borderRadius: 2, maxWidth: 160, backgroundColor: theme.palette.grey[200] }}
                        />
                    )}
                </>
            </Box>

            <Button
                sx={{ gridArea: 'button', justifySelf: 'end', alignSelf: 'start', mt: 0.5, whiteSpace: 'nowrap', ...PHONE_TAP_TARGET }}
                variant="outlined"
                size="small"
                startIcon={<TrackChangesIcon />}
                onClick={openGoals}
                disabled={!hasChecklist}
            >
                View Goals
            </Button>
        </Box>
    );
}

StudentAssessmentRow.propTypes = {
    // A StudentAssessmentRow from GET assessments/list
    row: PropTypes.shape({
        studentId: PropTypes.string.isRequired,
        groupId: PropTypes.string,
        studentName: PropTypes.string,
        subject: PropTypes.number.isRequired,
        grade: PropTypes.string,
        joinedOn: PropTypes.string,
        assessmentType: PropTypes.number,
        score: PropTypes.string,
        previousScore: PropTypes.string,
        // The term previousScore comes from
        previousTerm: PropTypes.number,
        blocked: PropTypes.bool,
        // A score is saved for this term but no type, so the student is still blocked
        typeRequired: PropTypes.bool,
        // This term's saved ratings above NT; null until goals are saved this term
        ratedCount: PropTypes.number,
        standardCount: PropTypes.number
    }).isRequired,
    term: PropTypes.shape({ id: PropTypes.string.isRequired, term: PropTypes.number.isRequired, startDate: PropTypes.string }).isRequired,
    onSaved: PropTypes.func.isRequired,
    onSavingChange: PropTypes.func.isRequired
};
